import Delete from '@mui/icons-material/Delete'
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { DocComment } from '@/types/comments'
import { parseCommentBody } from '@/utils/comment-tags'
import { getIdleTime, prettifyDate } from '@/utils/time'
import UserAvatar from '@/components/avatars/UserAvatar'

type CommentListItemProps = {
  comment: DocComment
  /** Author display name, resolved up front by the list (see useUsersById). */
  authorName?: string | null
  authorLoading?: boolean
  /** Whether the current user authored this comment. Only they may delete it. */
  isOwn?: boolean
  onDeleteClick?: () => void
}

export default function CommentListItem({
  comment,
  authorName,
  authorLoading = false,
  isOwn = false,
  onDeleteClick,
}: CommentListItemProps) {
  const isDeleted = !!comment.deleted_at

  const relativeTime = comment.created_at
    ? `${getIdleTime(comment.created_at, false, false)} ago`
    : ''
  const absoluteTime = comment.created_at
    ? prettifyDate(comment.created_at)
    : ''

  return (
    <Stack direction="row" spacing={1.5} sx={{ px: 2, py: 0.75 }}>
      <UserAvatar name={authorName} loading={authorLoading} />
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          {authorLoading ? (
            <Skeleton variant="text" width={96} />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {authorName || 'Unknown user'}
            </Typography>
          )}
          {relativeTime && (
            <Tooltip title={absoluteTime} disableInteractive>
              <Typography variant="caption" color="text.secondary">
                {relativeTime}
              </Typography>
            </Tooltip>
          )}
          <Stack sx={{ flex: 1 }} />
          {isOwn && !isDeleted && (
            <Tooltip title="Delete comment" disableInteractive>
              <IconButton
                size="small"
                aria-label="Delete comment"
                onClick={onDeleteClick}
                sx={{
                  color: 'text.disabled',
                  '&:hover': { color: 'error.main' },
                  alignSelf: 'center',
                }}
              >
                <Delete sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {isDeleted ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            [deleted]
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {parseCommentBody(comment.body ?? '').map((segment, index) =>
              segment.type === 'text' ? (
                segment.text
              ) : (
                <Box
                  key={index}
                  component="span"
                  sx={{ color: 'primary.main', fontWeight: 500 }}
                >
                  @{segment.name}
                </Box>
              ),
            )}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
